import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';

export type UserRole = 'super_admin' | 'farm_manager' | 'employee' | 'sales_officer' | 'accountant' | 'platform_admin';

export interface User {
  id: string;
  email: string;
  role: UserRole;
  status: string;
  first_name: string;
  last_name: string;
  phone?: string;
  farm_id?: string;
  must_change_password: boolean;
  farms?: { id: string; name: string; code: string; logo_url?: string; primary_color: string; secondary_color: string };
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('auth_token'));
  const [isLoading, setIsLoading] = useState(true);

  // ── Track whether initial load has happened ───────────────────
  const initializedRef = useRef(false);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.data);
    } catch {
      setToken(null);
      setUser(null);
      localStorage.removeItem('auth_token');
    }
  }, []); // ── Empty deps: api is stable, setters are stable ──

  // ── Only run on mount and when token changes ──────────────────
  useEffect(() => {
    if (initializedRef.current) return; // prevent double-run in StrictMode
    initializedRef.current = true;

    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      refreshUser().finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []); // ── Intentionally empty: only runs on mount ──

  const login = async (email: string, password: string) => {
    const { data } = await api.post('/auth/login', { email, password });
    const { token: newToken, user: userData } = data.data;
    localStorage.setItem('auth_token', newToken);
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
  };

  const logout = useCallback(() => {
    api.post('/auth/logout').catch(() => {});
    localStorage.removeItem('auth_token');
    delete api.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    initializedRef.current = false;
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      token,
      isLoading,
      isAuthenticated: !!user,
      login,
      logout,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

// Permission helpers
export const canManageUsers = (role: UserRole) => role === 'super_admin';
export const canManageFarms = (role: UserRole) => role === 'super_admin';
export const canViewFinancials = (role: UserRole) => ['super_admin', 'farm_manager', 'accountant'].includes(role);
export const canManageSales = (role: UserRole) => ['super_admin', 'farm_manager', 'sales_officer'].includes(role);
export const canViewReports = (role: UserRole) => ['super_admin', 'farm_manager', 'accountant'].includes(role);
// Matches backend's authorizeManager — flock/bird-stock management, same
// tier as farm management itself.
export const isManager = (role: UserRole) => ['super_admin', 'farm_manager'].includes(role);
// Matches backend's authorizeCollection — every operational role except
// sales_officer, who only sells stock others collect/manage.
export const canManageInventory = (role: UserRole) => role !== 'sales_officer';